/**
 * Read-only renderers for the exact decimal strings inventory puts on the
 * wire. Every one formats through `formatQuantity` (BigInt + formatToParts) —
 * a wire decimal never passes through `Number()`. A malformed value renders
 * raw rather than crashing the screen.
 */
import { useTranslation } from "react-i18next";
import { cn } from "@erp/ui";

import { formatQuantity } from "@/lib/format";

import {
  AMOUNT_FRACTION_DIGITS,
  QUANTITY_FRACTION_DIGITS,
  UNIT_COST_FRACTION_DIGITS,
} from "../api";
import { isNegativeDecimal, isPositiveDecimal } from "../decimal";

function useFormattedDecimal(value: string, fractionDigits: number): string {
  const { i18n } = useTranslation();
  try {
    return formatQuantity(value, i18n.language, fractionDigits);
  } catch {
    return value;
  }
}

export interface DecimalTextProps {
  value: string;
  className?: string;
  /** Rendered after the number at the logical end (base UoM code, %, …). */
  suffix?: string;
}

/** NUMERIC(20,6) quantity. */
export function QuantityText({ value, className, suffix }: DecimalTextProps) {
  const formatted = useFormattedDecimal(value, QUANTITY_FRACTION_DIGITS);
  return (
    <span className={cn("tabular-nums", className)}>
      {/* physical-ok: numerals render LTR even inside RTL text */}
      <span dir="ltr">{formatted}</span>
      {suffix === undefined ? null : <span className="ms-1 text-muted-foreground">{suffix}</span>}
    </span>
  );
}

/** NUMERIC(20,4) inventory value, stated in the company base currency. */
export function AmountText({ value, className, suffix }: DecimalTextProps) {
  const formatted = useFormattedDecimal(value, AMOUNT_FRACTION_DIGITS);
  return (
    <span className={cn("tabular-nums", className)}>
      <span dir="ltr">{formatted}</span>
      {suffix === undefined ? null : <span className="ms-1 text-muted-foreground">{suffix}</span>}
    </span>
  );
}

/** NUMERIC(20,6) unit cost. */
export function UnitCostText({ value, className, suffix }: DecimalTextProps) {
  const formatted = useFormattedDecimal(value, UNIT_COST_FRACTION_DIGITS);
  return (
    <span className={cn("tabular-nums", className)}>
      <span dir="ltr">{formatted}</span>
      {suffix === undefined ? null : <span className="ms-1 text-muted-foreground">{suffix}</span>}
    </span>
  );
}

/**
 * Unbounded-scale ratio (average unit value, days of cover, average daily
 * demand). The server states it exactly; the display rounds only what it
 * shows, never what it stores.
 */
export function RatioText({
  value,
  className,
  suffix,
  fractionDigits = 4,
}: DecimalTextProps & { fractionDigits?: number }) {
  const formatted = useFormattedDecimal(value, fractionDigits);
  return (
    <span className={cn("tabular-nums", className)}>
      <span dir="ltr">{formatted}</span>
      {suffix === undefined ? null : <span className="ms-1 text-muted-foreground">{suffix}</span>}
    </span>
  );
}

/**
 * Signed quantity with an explicit sign glyph and direction colour — the
 * movement ledger and the variance column read at a glance.
 */
export function SignedQuantityText({ value, className, suffix }: DecimalTextProps) {
  const { i18n } = useTranslation();
  const positive = isPositiveDecimal(value);
  const negative = isNegativeDecimal(value);
  let formatted: string;
  try {
    formatted = formatQuantity(value, i18n.language, QUANTITY_FRACTION_DIGITS);
  } catch {
    formatted = value;
  }
  return (
    <span
      className={cn(
        "tabular-nums",
        positive && "text-emerald-600 dark:text-emerald-400",
        negative && "text-destructive",
        className,
      )}
    >
      <span dir="ltr">
        {positive ? "+" : ""}
        {formatted}
      </span>
      {suffix === undefined ? null : <span className="ms-1 text-muted-foreground">{suffix}</span>}
    </span>
  );
}
